import { type ComponentType, type ReactElement } from 'react';
import { retryImport } from '@doxyme/ui/utils';
import type { NextPageWithLayout } from './pageTypes';
import { getLogger } from '../libs/logger';

const logger = getLogger('lazyPage');

type PageModule = { default: NextPageWithLayout };

/**
 * Adapts a page module for use as a React Router 7 route. The page's `getLayout`
 * chain is applied exactly as the Pages Router did, so routes keep identical layout
 *
 * Returned as an RR7 `lazy` loader so each page is code-split and the route
 * tree can be imported (e.g. for matching) without eagerly importing every
 * page module. The import is wrapped with `retryImport` so a transient or
 * stale-deploy chunk failure is retried before the route's `ErrorBoundary`
 * (`RouteErrorBoundary`) takes over — this is the route-level home for the
 * chunk-load resilience that used to live in per-view `lazyWithRetry` wrappers.
 */
export const lazyPage =
  (importPage: () => Promise<PageModule>) =>
  async (): Promise<{ Component: ComponentType }> => {
    const { default: Page } = await retryImport(importPage, {
      onSuccessAfterRetry: ({ attempts }) =>
        logger.info('page import succeeded after retry', { attempts }),
      onFinalFailure: ({ attempts, error }) =>
        logger.error('page import failed permanently', { attempts, error }),
    });
    const applyLayout = Page.getLayout ?? ((page: ReactElement) => page);

    const Component = (): ReactElement => <>{applyLayout(<Page />)}</>;
    Component.displayName = `RoutePage(${Page.displayName ?? Page.name ?? 'Anonymous'})`;

    return { Component };
  };
